import {IconButton} from '@dropbox/dig-components/buttons';
import {Chip} from '@dropbox/dig-components/chip';
import {SegmentedControl} from '@dropbox/dig-components/controls';
import {DatePickerInput} from '@dropbox/dig-components/date_picker';
import {FormLabel, FormRow} from '@dropbox/dig-components/form_row';
import {TextInput} from '@dropbox/dig-components/text_fields';
import {Toggletip, Tooltip} from '@dropbox/dig-components/tooltips';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Split, Stack, withShade} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {
  AddLine,
  ArrowRightLine,
  ChecklistLine,
  CheckmarkCircleFill,
  ChevronDownLine,
  ChevronRightLine,
  DeleteLine,
  MetricsLine,
  MilestoneLine,
  WorkstreamsLine,
} from '@dropbox/dig-icons/assets';
import {
  Employee,
  EmployeeLdap,
  EmployeeRoleLevel,
  WorkstreamDependency,
  WorkstreamDependencyType,
  WorkstreamMetric,
  WorkstreamMetricVisibility,
} from 'client';
import {Avatar} from 'components/DSYS/Avatar';
import {MnpiMetricIndicator} from 'components/DSYS/MnpiMetricIndicator';
import {getStatusStyle} from 'components/DSYS/StatusButtonIcon';
import {PeopleSearchMenu} from 'components/shared/PeopleSearchMenu';
import {useOutsideClick} from 'hooks/useOutsideClick';
import {t} from 'i18next';
import {useCallback, useEffect, useRef, useState} from 'react';
import {toCalendarDate, toEODISOStringFromDate} from 'views/admin/utils';

import {
  getSoonestMetricDate,
  getSoonestMetricTarget,
  getWorkstreamDri,
  isMetricLongterm,
  isWorkstreamMetric,
  toShortDate,
} from './utils';
import styles from './WorkstreamMetricEditor.module.css';
import {sortMetricsByCompleteDate} from './workstreamMetricSort';
import {WorkstreamTypeahead} from './WorkstreamTypeahead';

export const MetricEditor = ({
  isInvalid,
  workstreamId,
  dri,
  autofocus,
  metrics,
  updateMetrics,
}: {
  isInvalid?: boolean;
  workstreamId: number;
  dri?: EmployeeRoleLevel;
  autofocus?: boolean;
  metrics: WorkstreamMetric[];
  updateMetrics: (metrics: WorkstreamMetric[]) => void;
}) => {
  const [expanded, setExpanded] = useState<number | undefined>(autofocus ? -1 : undefined);
  const isFirstRender = useRef(true);

  const addMetric = useCallback(
    (collapse?: boolean) => {
      const id = metrics.length * -1 - 1;
      updateMetrics([
        ...metrics,
        {
          id,
          type: 'milestone',
          visibility: WorkstreamMetricVisibility.FULL,
          is_longterm: true,
          is_mnpi: false,
          title: '',
          current: null,
          target: null,
          complete: false,
          complete_date: null,
          url: null,
          owner: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          workstream_metric_links: [
            {
              id,
              workstream_id: workstreamId,
              order: metrics.length + 1,
              is_primary: false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            },
          ],
        },
      ]);

      if (!collapse) {
        setExpanded(id);
      }
    },
    [metrics, updateMetrics, workstreamId]
  );

  useEffect(() => {
    if (isFirstRender.current && autofocus) {
      addMetric(true);
      isFirstRender.current = false;
    }
  }, [addMetric, autofocus]);

  const removeMetric = (id: number) =>
    updateMetrics(metrics?.filter((metric) => metric.id !== id) ?? []);

  const handleMetricChange = (
    id: number,
    update: Partial<WorkstreamMetric | WorkstreamDependency>
  ) =>
    updateMetrics(
      metrics.map((metric) =>
        metric.id === id ? {...metric, ...update} : metric
      ) as WorkstreamMetric[]
    );

  return (
    <Stack gap="8">
      {metrics?.sort(sortMetricsByCompleteDate).map((metric, index) => {
        // Skip rendering restricted MNPI metrics
        if ((metric as WorkstreamMetric & {_isRestricted?: boolean})._isRestricted) {
          return null;
        }

        return (
          <MetricRow
            key={`metric-row-${metric.id}-${index}`}
            dri={dri}
            metric={metric}
            isInvalid={
              isInvalid &&
              metric.type === 'metric' &&
              (!metric.current?.length || !getSoonestMetricTarget(metric)?.length)
            }
            allWorkstreamIds={[workstreamId]}
            autofocus={metric.id < 0}
            removeMetric={removeMetric}
            handleMetricChange={handleMetricChange}
            expanded={expanded}
            setExpanded={setExpanded}
          />
        );
      })}

      <IconButton variant="outline" size="small" onClick={() => addMetric()}>
        <UIIcon src={AddLine} className={atoms({color: 'Border Base'})} />
      </IconButton>
    </Stack>
  );
};

export const MetricRow = ({
  dri,
  autofocus,
  isInvalid,
  metric,
  allWorkstreamIds,
  removeMetric,
  handleMetricChange,
  expanded,
  setExpanded,
}: {
  dri?: EmployeeRoleLevel;
  autofocus: boolean;
  isInvalid?: boolean;
  metric: WorkstreamMetric | WorkstreamDependency;
  allWorkstreamIds: number[];
  removeMetric: (id: number) => void;
  handleMetricChange:
    | ((id: number, update: Partial<WorkstreamMetric>) => void)
    | ((id: number, update: Partial<WorkstreamDependency>) => void);
  expanded: number | undefined;
  setExpanded: (id?: number) => void;
}) => {
  const [selectedOwner, setSelectedOwner] = useState<EmployeeLdap | undefined>(
    isWorkstreamMetric(metric) ? (metric.owner ?? undefined) : undefined
  );

  const [removedDri, setRemovedDri] = useState<boolean>(false);

  const {id, type} = metric;
  const title = isWorkstreamMetric(metric) ? metric.title : metric.dependent?.name;
  const complete = isWorkstreamMetric(metric) ? metric.complete : false;
  const etaDate = isWorkstreamMetric(metric)
    ? getSoonestMetricDate(metric)
    : metric.dependent?.end_date;
  const dependentDri = !isWorkstreamMetric(metric) ? getWorkstreamDri(metric.dependent) : undefined;

  const focusRef = useRef<HTMLDivElement>(null);

  useOutsideClick(
    focusRef,
    () => {
      if (expanded === id) {
        setExpanded();
      }
    },
    [styles.outcome],
    true,
    'click'
  );

  const handleOwnerChange = (employee?: Employee) => {
    if (!employee) {
      setRemovedDri(true);
    }
    setSelectedOwner(employee);
    handleMetricChange(id, {owner: employee});
  };

  // Clear dependent property when changing away from CHILD type
  useEffect(() => {
    if (
      type !== WorkstreamDependencyType.DEPENDS_ON &&
      !isWorkstreamMetric(metric) &&
      metric.dependent
    ) {
      handleMetricChange(id, {dependent: undefined, title: ''});
    }
  }, [type, id, handleMetricChange, metric]);

  const isExpanded = expanded === id;

  const metricOwner = isWorkstreamMetric(metric)
    ? (selectedOwner ?? (!removedDri ? dri : undefined))
    : dependentDri;
  const showMnpiIndicator = isWorkstreamMetric(metric) && Boolean(metric.is_mnpi);
  const displayType = type === 'deliverable' ? 'milestone' : type;

  return (
    <Box
      backgroundColor="Background Base"
      borderRadius="Medium"
      boxShadow={isExpanded ? 'Raised' : undefined}
      borderColor={isInvalid ? 'Alert Base' : 'Border Subtle'}
      borderWidth="1"
      borderStyle="Solid"
      position="relative"
      className={styles.outcome}
    >
      <Box ref={focusRef}>
        <Stack>
          <Box
            borderBottom={isExpanded ? 'Solid' : undefined}
            borderColor="Border Subtle"
            cursor="pointer"
            padding="12"
            onClick={() => setExpanded(isExpanded ? undefined : id)}
            {...withShade({className: styles.disableBorder})}
          >
            <Split alignY="center" gap="4">
              <Split.Item>
                <IconButton
                  shape="circular"
                  variant="borderless"
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    setExpanded(isExpanded ? undefined : id);
                  }}
                >
                  <UIIcon
                    src={isExpanded ? ChevronDownLine : ChevronRightLine}
                    className={atoms({color: 'Text Subtle'})}
                  />
                </IconButton>
              </Split.Item>

              <Split.Item paddingTop="4">{title && <MetricIcon metric={metric} />}</Split.Item>
              <Split.Item width="fill">
                <Text color={title ? undefined : 'subtle'} size="large">
                  {title || t('workstream_outcome_empty')}
                </Text>
                {etaDate && (
                  <Text color={!complete && new Date(etaDate) < new Date() ? 'error' : 'subtle'}>
                    {' '}
                    {t(`workstream_date${complete ? '_done' : ''}`, {
                      date: toShortDate(new Date(etaDate)),
                    })}
                  </Text>
                )}
              </Split.Item>
              {isWorkstreamMetric(metric) && type === 'metric' && (
                <Split.Item paddingRight="8">
                  <Text color="disabled" size="small" style={{whiteSpace: 'nowrap'}}>
                    {getSoonestMetricTarget(metric) && metric.current
                      ? `${metric.current} / ${getSoonestMetricTarget(metric)}`
                      : t('workstream_metric_empty')}
                  </Text>
                </Split.Item>
              )}

              {metricOwner && (
                <Split.Item>
                  <Box className={styles.metricOwnerWithMnpi}>
                    <Tooltip title={metricOwner?.name ?? ''}>
                      <Box>
                        {metricOwner && <Avatar user={metricOwner as EmployeeLdap} size="small" />}
                      </Box>
                    </Tooltip>
                    {showMnpiIndicator ? (
                      <MnpiMetricIndicator className={styles.metricMnpiIndicator} />
                    ) : null}
                  </Box>
                </Split.Item>
              )}

              {complete && (
                <Split.Item>
                  <IconButton
                    disabled
                    shape="circular"
                    variant="borderless"
                    onClick={() => removeMetric(id)}
                  >
                    <UIIcon
                      src={CheckmarkCircleFill}
                      style={{color: getStatusStyle('complete').color}}
                    />
                  </IconButton>
                </Split.Item>
              )}

              <Split.Item>
                <IconButton shape="circular" variant="borderless" onClick={() => removeMetric(id)}>
                  <UIIcon src={DeleteLine} className={atoms({color: 'Border Base'})} />
                </IconButton>
              </Split.Item>
            </Split>
          </Box>

          {isExpanded && (
            <Stack
              className={atoms({paddingX: '24', paddingTop: '16', paddingBottom: '24'})}
              gap="8"
            >
              {type !== WorkstreamDependencyType.DEPENDS_ON ? (
                <>
                  <FormRow>
                    <Box display="flex" alignItems="center" style={{gap: 16}}>
                      <SegmentedControl
                        selectedValue={displayType}
                        onSelection={(value) =>
                          value &&
                          handleMetricChange(id, {
                            type: value as unknown as WorkstreamDependencyType,
                            is_longterm: isMetricLongterm(value as string),
                          })
                        }
                      >
                        <SegmentedControl.Option variant="text" value="milestone">
                          <UIIcon src={MilestoneLine} /> {t('milestone')}
                        </SegmentedControl.Option>
                        <SegmentedControl.Option variant="text" value="metric">
                          <UIIcon src={MetricsLine} /> {t('metric')}
                        </SegmentedControl.Option>
                      </SegmentedControl>
                      {isMetricLongterm(type) && (
                        <Chip size="medium" isSelected={true}>
                          <Chip.IconAccessory>
                            <UIIcon src={ChecklistLine} />
                          </Chip.IconAccessory>
                          <Chip.Content>{t('longterm_outcomes')}</Chip.Content>
                        </Chip>
                      )}
                    </Box>
                  </FormRow>

                  <FormRow>
                    <FormLabel>
                      <Box display="flex" alignItems="flex-end" style={{gap: 4}}>
                        <Text isBold size="large" style={{marginBottom: -1}}>
                          {t(displayType)}
                        </Text>
                        <Text size="small" color="subtle">
                          {t(`workstream_${displayType}_subtext`)}
                        </Text>
                      </Box>
                    </FormLabel>
                    <TextInput
                      placeholder={t('start_writing_placeholder')}
                      autoFocus={autofocus}
                      onFocus={() => setExpanded(id)}
                      value={title}
                      onChange={(e) => handleMetricChange(id, {title: e.target.value})}
                    />
                  </FormRow>
                </>
              ) : (
                <FormRow>
                  <FormLabel subtext={<Toggletip title={t(`workstream_${type}_subtext`)} />}>
                    <Box display="flex" alignItems="flex-end" style={{gap: 4}}>
                      <Text isBold size="large" style={{marginBottom: -1}}>
                        {t(type)}
                      </Text>
                      <Text size="small" color="subtle">
                        {t(`workstream_${type}_subtext`)}
                      </Text>
                    </Box>
                  </FormLabel>
                  <WorkstreamTypeahead
                    valueFallback={title}
                    selectedIds={allWorkstreamIds}
                    onSelect={(workstream) => {
                      handleMetricChange(id, {dependent: {...workstream, metrics: []}});
                    }}
                  />
                </FormRow>
              )}

              {isWorkstreamMetric(metric) && type === 'metric' ? (
                <Split alignY="center" gap="8">
                  <Split.Item width="fill">
                    <FormRow>
                      <FormLabel>{t('current')}</FormLabel>
                      <TextInput
                        isInvalid={isInvalid && !metric.current?.length}
                        onFocus={() => setExpanded(id)}
                        placeholder={t('add_value')}
                        value={metric.current ?? ''}
                        onChange={(e) => handleMetricChange(id, {current: e.target.value})}
                      />
                    </FormRow>
                  </Split.Item>
                  <Split.Item paddingTop="8">
                    <UIIcon src={ArrowRightLine} className={atoms({color: 'Border Subtle'})} />
                  </Split.Item>
                  <Split.Item width="fill">
                    <FormRow>
                      <FormLabel>{t('target')}</FormLabel>
                      <TextInput
                        isInvalid={isInvalid && !getSoonestMetricTarget(metric)?.length}
                        onFocus={() => setExpanded(id)}
                        placeholder={t('add_value')}
                        value={getSoonestMetricTarget(metric) ?? ''}
                        onChange={(e) => handleMetricChange(id, {target: e.target.value})}
                      />
                    </FormRow>
                  </Split.Item>
                </Split>
              ) : null}

              {(type !== WorkstreamDependencyType.DEPENDS_ON || dependentDri) && (
                <Split gap="16">
                  <Split.Item width="fill">
                    <FormRow>
                      <FormLabel>{t('workstream_outcome_dri')}</FormLabel>
                      <EmployeeSelect
                        disabled={type === WorkstreamDependencyType.DEPENDS_ON}
                        selected={metricOwner}
                        onSelect={handleOwnerChange}
                      />
                    </FormRow>
                  </Split.Item>
                  <Split.Item>
                    <Box
                      backgroundColor="Border Base"
                      style={{height: 48, width: 1, marginTop: 32}}
                    />
                  </Split.Item>
                  <Split.Item width="fill">
                    <FormRow>
                      <FormLabel>{t('workstream_edit_end_date')}</FormLabel>
                      <DatePickerInput
                        disabled={type === WorkstreamDependencyType.DEPENDS_ON}
                        id={`choose-delivery-${id}`}
                        autocomplete="off"
                        placeholder={isWorkstreamMetric(metric) ? t('pick_a_date') : 'No ETA'}
                        value={etaDate ? toCalendarDate(etaDate) : null}
                        onChange={(date) =>
                          handleMetricChange(id, {complete_date: toEODISOStringFromDate(date)})
                        }
                      />
                    </FormRow>
                  </Split.Item>
                </Split>
              )}
            </Stack>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export const MetricIcon = ({
  size = 'medium',
  metric,
}: {
  size?: 'small' | 'medium';
  metric: WorkstreamMetric | WorkstreamDependency;
}) => (
  <UIIcon
    src={
      isWorkstreamMetric(metric)
        ? metric.type === 'milestone'
          ? MilestoneLine
          : metric.type === 'deliverable'
            ? MilestoneLine
            : MetricsLine
        : WorkstreamsLine
    }
    className={atoms({color: 'Text Subtle', flexShrink: 0})}
    size={size}
  />
);

const EmployeeSelect = ({
  disabled,
  selected,
  onSelect,
}: {
  disabled?: boolean;
  selected?: EmployeeLdap;
  onSelect: (employee?: Employee) => void;
}) => {
  // add default selected employees

  const selectEmployee = (employee: Employee) => {
    onSelect(employee);
  };

  const selectEmployees = (employees: Employee[]) => {
    if (employees.length > 0) {
      onSelect(employees[0]);
    }
  };

  const removeEmployee = () => {
    onSelect();
  };

  return (
    <PeopleSearchMenu
      typeaheadContext="workstream_filter"
      disable={disabled}
      selectedEmployees={selected ? [selected as Employee] : []}
      onRemoveEmployee={removeEmployee}
      onSelectEmployee={selectEmployee}
      onSelectEmployees={selectEmployees}
      showReportingLine={true}
      isSingleSelect={true}
      searchIconPosition="left"
      allowSelf={true}
    />
  );
};
